// --- Unsold Players Queue View ---
import { useState, useMemo, useEffect } from 'react';
import { Clock, Search, ArrowRight, ArrowLeft, ArrowLeftRight, Users, RotateCcw } from 'lucide-react';
import Pagination from './Pagination';
import PlayerCard from './PlayerCard';
import PhotoLightbox from './PhotoLightbox';
import type { Player, GenderFilter, SideFilter, Team, AppUser } from '../types';

interface UnsoldQueueProps {
  players: Player[];
  teams: Team[];
  currentUser: AppUser;
  onManagePlayer: (player: Player) => void;
}

interface FilterCardProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  totalCount?: number;
  active: boolean;
  onClick: () => void;
  color?: 'indigo' | 'amber' | 'pink' | 'blue' | 'green' | 'purple';
}

const FilterCard = ({ icon, label, count, totalCount, active, onClick, color = 'indigo' }: FilterCardProps) => {
  const colorStyles = {
    indigo: 'bg-indigo-100 text-indigo-600',
    amber: 'bg-amber-100 text-amber-600',
    pink: 'bg-pink-100 text-pink-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
        active
          ? 'border-indigo-500 bg-white shadow-md'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorStyles[color]}`}>
        {icon}
      </div>
      <div className="text-left">
        <p className="text-xs text-slate-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-slate-800">
          {count}
          {totalCount !== undefined && totalCount !== count && (
            <span className="text-sm text-slate-400 font-normal ml-1">/ {totalCount}</span>
          )}
        </p>
      </div>
    </button>
  );
};

const UnsoldQueue: React.FC<UnsoldQueueProps> = ({ players, teams, currentUser, onManagePlayer }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [genderFilter, setGenderFilter] = useState<GenderFilter>('all');
  const [sideFilter, setSideFilter] = useState<SideFilter>('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxPlayer, setLightboxPlayer] = useState<Player | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(18);

  // Get unsold players only
  const unsoldPlayers = useMemo(() => players.filter(p => p.status === 'unsold'), [players]);

  // Calculate totals for stat cards
  const totalCounts = useMemo(() => {
    const male = unsoldPlayers.filter(p => p.details?.player_gender?.toLowerCase() === 'male').length;
    const female = unsoldPlayers.filter(p => p.details?.player_gender?.toLowerCase() === 'female').length;
    const right = unsoldPlayers.filter(p => p.details?.playing_side?.toLowerCase() === 'right').length;
    const left = unsoldPlayers.filter(p => p.details?.playing_side?.toLowerCase() === 'left').length;
    const both = unsoldPlayers.filter(p => p.details?.playing_side?.toLowerCase() === 'both').length;
    return { total: unsoldPlayers.length, male, female, right, left, both };
  }, [unsoldPlayers]);

  // Dynamic counts that respect the opposite filter (same logic as Players view)
  const dynamicCounts = useMemo(() => {
    const sideFiltered = sideFilter === 'all'
      ? unsoldPlayers
      : unsoldPlayers.filter(p => p.details?.playing_side?.toLowerCase() === sideFilter);

    const genderFiltered = genderFilter === 'all'
      ? unsoldPlayers
      : unsoldPlayers.filter(p => p.details?.player_gender?.toLowerCase() === genderFilter);

    return {
      total: unsoldPlayers.length,
      male: sideFiltered.filter(p => p.details?.player_gender?.toLowerCase() === 'male').length,
      female: sideFiltered.filter(p => p.details?.player_gender?.toLowerCase() === 'female').length,
      right: genderFiltered.filter(p => p.details?.playing_side?.toLowerCase() === 'right').length,
      left: genderFiltered.filter(p => p.details?.playing_side?.toLowerCase() === 'left').length,
      both: genderFiltered.filter(p => p.details?.playing_side?.toLowerCase() === 'both').length,
    };
  }, [unsoldPlayers, genderFilter, sideFilter]);

  // Apply filters
  const filteredPlayers = useMemo(() => {
    return unsoldPlayers.filter(p => {
      if (genderFilter !== 'all') {
        const gender = p.details?.player_gender?.toLowerCase();
        if (gender !== genderFilter) return false;
      }
      if (sideFilter !== 'all') {
        const side = p.details?.playing_side?.toLowerCase();
        if (side !== sideFilter) return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return (
          p.name.toLowerCase().includes(q) ||
          p.mobile_number?.includes(q) ||
          p.details?.player_full_name?.toLowerCase().includes(q) ||
          p.details?.jersey_name?.toLowerCase().includes(q) ||
          p.details?.jersey_number?.includes(q)
        );
      }
      return true;
    });
  }, [unsoldPlayers, genderFilter, sideFilter, searchQuery]);

  // Sort after filtering
  const sortedPlayers = useMemo(() => {
    const result = [...filteredPlayers];

    // Default sort by name ascending
    result.sort((a, b) => {
      const aVal = a.name.toLowerCase();
      const bVal = b.name.toLowerCase();
      return aVal.localeCompare(bVal);
    });

    return result;
  }, [filteredPlayers]);

  // Paginate filtered players
  const paginatedPlayers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedPlayers.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedPlayers, currentPage, itemsPerPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, genderFilter, sideFilter]);

  const resetFilters = () => {
    setGenderFilter('all');
    setSideFilter('all');
    setSearchQuery('');
  };

  const isFiltersActive = genderFilter === 'all' && sideFilter === 'all';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">Unsold Players Queue</h2>
              <p className="text-white/80">{unsoldPlayers.length} players waiting for auction</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold">{filteredPlayers.length}</p>
            <p className="text-white/70 text-sm">Showing</p>
          </div>
        </div>
      </div>

      {/* Active Filters Summary */}
      {(!isFiltersActive || searchQuery) && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-slate-500">Active filters:</span>
          {genderFilter !== 'all' && (
            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-sm rounded-full">
              {genderFilter === 'male' ? 'Male' : 'Female'}
            </span>
          )}
          {sideFilter !== 'all' && (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded-full capitalize">
              {sideFilter} Side
            </span>
          )}
          {searchQuery && (
            <span className="px-2 py-1 bg-slate-100 text-slate-700 text-sm rounded-full">
              "{searchQuery}"
            </span>
          )}
          <span className="text-sm font-medium text-indigo-600 ml-2">
            Showing {filteredPlayers.length} of {unsoldPlayers.length} unsold players
          </span>
          <button
            onClick={resetFilters}
            className="ml-auto flex items-center gap-1 px-3 py-1 text-sm text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" /> Reset All
          </button>
        </div>
      )}

      {/* Filter Cards - Gender */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FilterCard
          icon={<Users className="w-5 h-5" />}
          label="All Unsold"
          count={filteredPlayers.length}
          totalCount={totalCounts.total}
          active={isFiltersActive}
          onClick={resetFilters}
          color="indigo"
        />
        <FilterCard
          icon={<Users className="w-5 h-5" />}
          label="Male Players"
          count={dynamicCounts.male}
          totalCount={totalCounts.male}
          active={genderFilter === 'male'}
          onClick={() => setGenderFilter(genderFilter === 'male' ? 'all' : 'male')}
          color="amber"
        />
        <FilterCard
          icon={<Users className="w-5 h-5" />}
          label="Female Players"
          count={dynamicCounts.female}
          totalCount={totalCounts.female}
          active={genderFilter === 'female'}
          onClick={() => setGenderFilter(genderFilter === 'female' ? 'all' : 'female')}
          color="pink"
        />
      </div>

      {/* Filter Cards - Playing Side */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FilterCard
          icon={<ArrowRight className="w-5 h-5" />}
          label="Right Side"
          count={dynamicCounts.right}
          totalCount={totalCounts.right}
          active={sideFilter === 'right'}
          onClick={() => setSideFilter(sideFilter === 'right' ? 'all' : 'right')}
          color="blue"
        />
        <FilterCard
          icon={<ArrowLeft className="w-5 h-5" />}
          label="Left Side"
          count={dynamicCounts.left}
          totalCount={totalCounts.left}
          active={sideFilter === 'left'}
          onClick={() => setSideFilter(sideFilter === 'left' ? 'all' : 'left')}
          color="green"
        />
        <FilterCard
          icon={<ArrowLeftRight className="w-5 h-5" />}
          label="Both Sides"
          count={dynamicCounts.both}
          totalCount={totalCounts.both}
          active={sideFilter === 'both'}
          onClick={() => setSideFilter(sideFilter === 'both' ? 'all' : 'both')}
          color="purple"
        />
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="flex-1 relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name, mobile, jersey, or full name..."
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>
      </div>

      {/* Players Grid */}
      {filteredPlayers.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
          <Clock className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          No unsold players found
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedPlayers.map(player => (
              <PlayerCard
                key={player.id}
                player={player}
                teams={teams}
                currentUser={currentUser}
                onManagePlayer={onManagePlayer}
                onPhotoClick={(p) => {
                  setLightboxPlayer(p);
                  setLightboxOpen(true);
                }}
              />
            ))}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredPlayers.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        </>
      )}

      {/* Photo Lightbox */}
      <PhotoLightbox
        imageUrl={lightboxPlayer?.details?.photo_url || ''}
        playerName={lightboxPlayer?.name || ''}
        isOpen={lightboxOpen}
        onClose={() => {
          setLightboxOpen(false);
          setLightboxPlayer(null);
        }}
      />
    </div>
  );
};

export default UnsoldQueue;
